/**
 * Export Ledger button — shared by Vendor Commissions + Commission
 * Invoices admin pages. Downloads a CSV: either a Monthly Ledger (all
 * vendors, one month) or a full Vendor Ledger (one vendor, all months,
 * with a running balance).
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../ui/sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Download, Loader2 } from 'lucide-react';

export default function ExportLedgerButton({ defaultMonth = '' }) {
  const { apiCall } = useAuth();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('month'); // 'month' | 'vendor'
  const [month, setMonth] = useState(defaultMonth);
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMonth(defaultMonth);
    apiCall('/api/admin/vendor-billing/vendors?limit=300').then(d => setVendors(d.vendors || [])).catch(() => {});
  }, [open]);

  async function handleExport() {
    if (mode === 'month' && !month) { toast.error('Pick a month'); return; }
    if (mode === 'vendor' && !vendorId) { toast.error('Pick a vendor'); return; }
    setDownloading(true);
    const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const params = mode === 'month' ? `month=${month}` : `vendor_id=${vendorId}`;
    try {
      const response = await fetch(`${BACKEND}/api/admin/commission-invoices/export/ledger?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Aniyor_Commission_Ledger_${mode === 'month' ? month : vendorId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ledger exported');
      setOpen(false);
    } catch (e) {
      toast.error('Failed to export ledger');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="export-ledger-btn">
        <Download className="h-4 w-4 mr-1.5" />
        Export Ledger
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="export-ledger-modal">
          <DialogHeader>
            <DialogTitle>Export Ledger</DialogTitle>
            <DialogDescription>Download a CSV of invoices + credit/debit notes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              {[{ id: 'month', label: 'Monthly Ledger (all vendors)' }, { id: 'vendor', label: 'Vendor Ledger (all time)' }].map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setMode(o.id)}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                    mode === o.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                  }`}
                  data-testid={`export-ledger-mode-${o.id}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {mode === 'month' ? (
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">Month</label>
                <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-9 text-sm" data-testid="export-ledger-month-input" />
              </div>
            ) : (
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 font-medium">Vendor</label>
                <select
                  className="w-full h-9 text-sm border border-gray-200 rounded-md px-2"
                  value={vendorId}
                  onChange={e => setVendorId(e.target.value)}
                  data-testid="export-ledger-vendor-select"
                >
                  <option value="">Select vendor…</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id}>{v.business_name || v.email}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="export-ledger-cancel-btn">Cancel</Button>
            <Button onClick={handleExport} disabled={downloading} data-testid="export-ledger-submit-btn">
              {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
              Download CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
